/**
 * Хук для управления состоянием авторизации пользователя
 * 
 * Предоставляет функционал для входа, выхода и проверки авторизации.
 * Автоматически проверяет наличие токена при монтировании и восстанавливает сессию.
 * Токен и данные пользователя хранятся в localStorage.
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { config } from '../config';
import * as authApi from '../api/auth.api';
import axios from 'axios';

/**
 * Интерфейс данных пользователя
 */
export interface User {
  /** Уникальный идентификатор пользователя */
  id: string;
  /** Номер телефона пользователя */
  phone: string;
  /** ФИО пользователя */
  fullName: string;
  /** Email пользователя (опционально) */
  email?: string;
  /** Telegram ID пользователя (опционально) */
  telegramId?: string;
}

/**
 * Интерфейс состояния авторизации
 */
export interface AuthState {
  /** Флаг авторизации пользователя */
  isAuthenticated: boolean;
  /** Данные авторизованного пользователя или null */
  user: User | null;
  /** Токен авторизации или null */
  token: string | null;
}

/**
 * Ключи для хранения данных в localStorage (из конфигурации)
 */
const TOKEN_KEY = config.storage.keys.authToken;
const USER_KEY = config.storage.keys.userData;

/**
 * Результат хука useAuth
 */
export interface UseAuthReturn {
  /** Флаг авторизации пользователя */
  isAuthenticated: boolean;
  /** Данные авторизованного пользователя или null */
  user: User | null;
  /** Токен авторизации или null */
  token: string | null;
  /** Флаг загрузки (проверка токена при инициализации) */
  isLoading: boolean;
  /** Функция для входа в систему */
  login: (phone: string, password: string) => Promise<void>;
  /** Функция для выхода из системы */
  logout: () => void;
  /** Функция для проверки валидности токена */
  checkAuth: () => Promise<void>;
}

/**
 * Хук для управления состоянием авторизации
 * 
 * Автоматически проверяет наличие токена при монтировании компонента.
 * Если токен найден и валиден, автоматически авторизует пользователя.
 * Предоставляет методы для входа, выхода и проверки авторизации.
 * 
 * @returns Объект с состоянием авторизации и функциями для работы с ней
 * 
 * @example
 * ```tsx
 * const { isAuthenticated, user, login, logout } = useAuth();
 * 
 * // Вход в систему
 * await login('+79001234567', 'password123');
 * 
 * // Проверка авторизации
 * if (isAuthenticated) {
 *   console.log('Пользователь:', user?.fullName);
 * }
 * 
 * // Выход из системы
 * logout();
 * ```
 */
export const useAuth = (): UseAuthReturn => {
  // Получаем токен из localStorage
  const [token, setToken] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(TOKEN_KEY);
  });

  // Используем useQuery для проверки токена
  const { data: user, isLoading, refetch } = useQuery({
    queryKey: ['auth', 'user', token],
    queryFn: async () => {
      if (!token) return null;
      try {
        return await authApi.checkToken(token);
      } catch (error) {
        // Токен невалиден - очищаем данные
        if (typeof window !== 'undefined') {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
        }
        setToken(null);
        return null;
      }
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 минут
    retry: false,
  });

  // Состояние авторизации
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
  });

  // Обновляем состояние при изменении user или token
  useEffect(() => {
    setAuthState({
      isAuthenticated: !!user && !!token,
      user: user || null,
      token: token || null,
    });
  }, [user, token]);

  /**
   * Функция для входа в систему
   * Вызывает API авторизации, сохраняет токен и данные пользователя
   * 
   * @param phone - Номер телефона пользователя
   * @param password - Пароль пользователя
   * @throws Error если авторизация не удалась
   */
  const login = useCallback(async (phone: string, password: string): Promise<void> => {
    try {
      // Вызываем API авторизации
      const response = await authApi.login(phone, password);

      // Сохраняем токен и данные пользователя в localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem(TOKEN_KEY, response.token);
        localStorage.setItem(USER_KEY, JSON.stringify(response.user));
      }

      // Обновляем токен для триггера useQuery
      setToken(response.token);

      // Принудительно обновляем данные пользователя
      await refetch();
    } catch (error) {
      // Пробрасываем ошибку для обработки в компоненте
      if (axios.isAxiosError(error)) {
        throw error;
      }
      throw new Error('Ошибка при авторизации');
    }
  }, [refetch]);

  /**
   * Функция для выхода из системы
   * Удаляет токен и данные пользователя из localStorage и состояния
   */
  const logout = useCallback((): void => {
    try {
      // Вызываем API выхода (не ждем ответа, выполняем асинхронно)
      authApi.logout().catch((error) => {
        console.error('Error during logout API call:', error);
      });
    } catch (error) {
      console.error('Error during logout:', error);
    }

    // Удаляем данные из localStorage
    if (typeof window !== 'undefined') {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    }

    // Очищаем токен для триггера useQuery
    setToken(null);
  }, []);

  /**
   * Функция для проверки валидности токена
   * Проверяет токен через API и обновляет состояние
   * 
   * @throws Error если токен невалиден
   */
  const checkAuth = useCallback(async (): Promise<void> => {
    await refetch();
  }, [refetch]);

  return {
    isAuthenticated: authState.isAuthenticated,
    user: authState.user,
    token: authState.token,
    isLoading,
    login,
    logout,
    checkAuth,
  };
};
